import {
  Rapportage,
  RapportageElement,
  RapportageKolommenLasten,
  RapportageKolommenNbi,
  RapportageKolomselectie,
  RapportageOutput,
  RapportageStructuur,
  StandaardRapportOptions
} from "../../.generated/instellingen-forms/instellingen-formstypes";
import { RapportageSamenstellenState } from "../../shared/components/rapportage/infra/rapportage-samenstellen-types";
import { RapportageElementSoort } from "../../shared/components/rapportage/infra/rapportage-structuur-types";
import {
  kolommenInstellenModalSchema,
  kolommenLastenSchema,
  kolommenNbiSchema,
  rapportageElementSchema,
  rapportageSamenstellenSchema,
  rapportageStructuurSchema
} from "../../shared/components/rapportage/rapportage-samenstellen-schema";
import { createMapToUi } from "../../shared/utils/create-map-to-ui";

const findElement = (elements: RapportageElement[] | null, targetCode: number): RapportageElement | null => {
  if (!elements) return null;
  for (const element of elements) {
    if (element.code === targetCode) {
      return element;
    }

    // If not found, search in nested 'elementen' recursively
    const foundInSubElement = findElement(element.elementen, targetCode);
    if (foundInSubElement) {
      return foundInSubElement;
    }
  }

  return null; // Return null if the code is not found
};

const mapRapportageElementenToUi: any = createMapToUi(rapportageElementSchema).from<
  RapportageElement,
  RapportageElement | undefined
>({
  code: v => v.code,
  geselecteerd: v => v.geselecteerd,
  naam: (v, def) => (v.naamWeergevenAlsTitel ? v.naam : def?.naam ?? v.naam),
  naamWeergevenAlsTitel: v => v.naamWeergevenAlsTitel,
  soortElement: v => (v.soortElement as unknown) as RapportageElementSoort,
  volgnummer: v => v.volgnummer,
  elementen: (v, def) =>
    v.elementen?.map(c => mapRapportageElementenToUi(c, findElement(def ? def.elementen : null, c.code))),
  kolommen: (v, def) =>
    def
      ? def.kolommen?.map(x => {
          return {
            kolomTitel: x,
            kolomNaam: x.replace(/ /g, ""),
            selected: v.kolommen?.includes(x.replace(/ /g, "")) ?? false
          };
        })
      : v.kolommen?.map(x => {
          return {
            kolomTitel: x,
            kolomNaam: x.replace(/ /g, ""),
            selected: false
          }; // default structuur
        }),
  inhoud: v => v.inhoud || null,
  perAdviesAanpasbaar: v => v.inhoudWijzigbaar,
  inhoudBeschikbaar: v => v.inhoudBeschikbaar ?? true,
  gebruikAi: v => v.gebruikAi,
  promptNaam: v => v.promptNaam || null,
  promptAanvulling: v => v.promptAanvulling || null
});

const mapKolommenLastenToUi = createMapToUi(kolommenLastenSchema).from<RapportageKolommenLasten>({
  brutoInkomen: v => v.brutoInkomen,
  brutoLast: v => v.brutoLast,
  fiscaalVoordeel: v => v.fiscaalVoordeel,
  huur: v => v.huur,
  hypotheekAflossing: v => v.hypotheekAflossing,
  kredietLast: v => v.kredietLast,
  nettoLast: v => v.nettoLast,
  opnameUitDepot: v => v.opnameUitDepot,
  overigeUitgaven: v => v.overigeUitgaven,
  rente: v => v.rente,
  somHypotheken: v => v.somHypotheken,
  spaarBeleggingsdepots: v => v.spaarBeleggingsdepots,
  uitkeringKapitaalverzekering: v => v.uitkeringKapitaalverzekering,
  verzekeringspremies: v => v.verzekeringspremies,
  vrh: v => v.vrh,
  waardeopbouw: v => v.waardeopbouw
});
const mapKolommenNbiToUi = createMapToUi(kolommenNbiSchema).from<RapportageKolommenNbi>({
  brutoLast: v => v.brutoLast,
  hypotheek: v => v.hypotheek,
  inkomstenbelasting: v => v.inkomstenbelasting,
  kredietLast: v => v.kredietLast,
  nbi: v => v.nbi,
  nettoInkomen: v => v.nettoInkomen,
  overigeUitgaven: v => v.overigeUitgaven,
  spaarBeleggingsdepots: v => v.spaarBeleggingsdepots,
  verzekeringen: v => v.verzekeringen,
  vrh: v => v.vrh
});

const mapRapportageKolomSelectieToUi = createMapToUi(kolommenInstellenModalSchema).from<RapportageKolomselectie>({
  kolommenLasten: v => mapKolommenLastenToUi(v.kolommenLasten),
  kolommenNbi: v => mapKolommenNbiToUi(v.kolommenNbi)
});

const mapRapportageStructuurToUi = createMapToUi(rapportageStructuurSchema).from<
  RapportageStructuur,
  RapportageStructuur | undefined
>({
  elementen: (v, z) => v.elementen.map(c => mapRapportageElementenToUi(c, findElement(z ? z.elementen : null, c.code))),
  kolomselectie: v => mapRapportageKolomSelectieToUi(v.kolomselectie),
  naam: v => v.naam,
  standaardRapport: v => {
    // Check if v.standaardRapport is one of the valid enum options
    if (v.standaardRapport && Object.values(StandaardRapportOptions).includes(v.standaardRapport)) {
      return v.standaardRapport;
    }
    return null;
  },
  structuurId: v => v.structuurId,
  volgnummer: v => v.volgnummer,
  isVerwijderbaar: v => v.isVerwijderbaar
});

const dl2ui = createMapToUi(rapportageSamenstellenSchema).from<Rapportage, RapportageStructuur | undefined>({
  structuren: (v, z) =>
    v.structuren
      .filter(x => x.standaardRapport !== StandaardRapportOptions.Standaardrapport)
      .map(c => mapRapportageStructuurToUi(c, z)),
  defaultStructuur: (_, z) => mapRapportageStructuurToUi(z, undefined),
  movetoparent: _ => null
});

export function mapRapportageSamenstellenDlToUi(data: RapportageOutput | null): RapportageSamenstellenState | null {
  const rapporageSamenstellen = data && data.rapportage ? data.rapportage : null;

  if (!rapporageSamenstellen) return null;

  const defaultStructuur = rapporageSamenstellen.structuren.find(
    c => c.standaardRapport === StandaardRapportOptions.Standaardrapport
  );

  const res = dl2ui(rapporageSamenstellen, defaultStructuur);

  return res;
}
